import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import './App.css';
import { AppAuth } from './API';
import logger from './utils/logger';
import { theme } from './styles/theme';

// Import components
import Home from './components/Home';
import SubmitGarden3D from './components/SubmitGarden3D';
import ViewPub from './components/ViewPub';
import FindPubs, { PubObject } from './components/FindPubs';
import SearchArea from './components/SearchArea';
import About from './components/About';
import AdminLogin from './components/AdminLogin';
import ModerationAdmin from './components/ModerationAdmin';
import AdminGuard from './components/AdminGuard';
import NotFound from './components/NotFound';
import { Background } from "./styles/Background";
import ScrollIndicator from './components/ScrollIndicator';
import CityLandingPage from './components/CityLandingPage';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import InstallPWA from './components/InstallPWA';

// Export the ScrollIndicator for easy use throughout the app
export { ScrollIndicator };

// Create a wrapper component to access the location state and URL params
const ViewPubWrapper = () => {
  const location = useLocation();
  const pubFromLocation = location.state?.pubToView || null;
  const { googlePlaceId } = useParams<{ googlePlaceId: string }>();
  
  // If we have a pub in location state, return ViewPub with that pub
  if (pubFromLocation) {
    // Store the pub ID for potential recovery (optional)
    localStorage.setItem('currentViewPubGPId', pubFromLocation.uid);
    return <ViewPub pubToView={pubFromLocation} />;
  }
  
  // If we have a googlePlaceId in the URL, fetch and return that pub
  if (googlePlaceId) {
    return <ViewPub googlePlaceId={googlePlaceId} />;
  }
  
  // Otherwise, show loading spinner
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh' 
    }}>
      <CircularProgress />
    </Box>
  );
};

const App: React.FC = () => {
  // App state
  const [title, setTitle] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [pubToView, setPubToView] = useState<PubObject | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showBurgerMenuIcon, setShowBurgerMenuIcon] = useState<boolean>(true);

  // Get stored coordinates from localStorage or use defaults
  const getStoredCoordinates = () => {
    const storedLat = localStorage.getItem('userLatitude');
    const storedLon = localStorage.getItem('userLongitude');
    const hasPermission = localStorage.getItem('locationPermissionGranted') === 'true';

    if (hasPermission && storedLat && storedLon) {
      return {
        latitude: parseFloat(storedLat),
        longitude: parseFloat(storedLon)
      };
    }

    // Fallback to defaults only if no stored coordinates
    return {
      latitude: 51.4700777,  // Default latitude
      longitude: -0.0245202  // Default longitude
    };
  };

  const initialCoords = getStoredCoordinates();
  const [latitude, setLatitude] = useState<number>(initialCoords.latitude);
  const [longitude, setLongitude] = useState<number>(initialCoords.longitude);

  // Initialize authentication when app loads
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Only initialize if we have no token at all
        // Let the interceptor handle expired tokens when actual API calls are made
        if (!AppAuth.getToken()) {
          //logger.info('No authentication token found, initializing');
          await AppAuth.initializeToken();
          //logger.info('Authentication token initialized successfully');
        }

        // Store the user's timezone
        import('./API').then(({ storeUserTimezone }) => {
          storeUserTimezone();
        });

      } catch (error) {
        logger.error('Failed to initialize authentication:', error);
      }
    };
    
    initAuth();
  }, []);

  // Set up the navigation guards and persistent state
  useEffect(() => {
    const currentViewPubGPId = localStorage.getItem('currentViewPubGPId');
    // Check if it exists, is not null/empty, and is not the literal string "undefined"
    if (currentViewPubGPId && typeof currentViewPubGPId === 'string' && currentViewPubGPId !== "undefined" && !pubToView) {
      console.log("App.tsx: Restoring pub data for ID:", currentViewPubGPId); // Keep this log for debugging initially
      import('./API').then(({ getPubByGooglePlaceId: getPubById }) => {
        getPubById(currentViewPubGPId) // Pass the validated ID
          .then(response => {
            // Ensure response is valid before setting state
            if (response?.data?.pub?.google_place_id) { // Check for essential data
              setPubToView(response.data.pub);
            } else {
               // Handle case where API returns success but no valid pub (e.g., pub deleted)
               console.warn(`App.tsx: Pub data not found or invalid for restored ID: ${currentViewPubGPId}`);
               localStorage.removeItem('currentViewPubGPId'); // Remove invalid stored ID
            }
          })
          .catch(error => {
            // Only log non-404 errors, as 404 might mean the pub was deleted
            if (!(error.response && error.response.status === 404)) {
               console.error("App.tsx: Error restoring pub data:", error);
            } else {
               console.log(`App.tsx: Stored pub ID ${currentViewPubGPId} not found (404), removing from storage.`);
            }
            localStorage.removeItem('currentViewPubGPId'); // Remove stored ID on error
          });
      });
    } else if (currentViewPubGPId === "undefined") {
        // Clean up the bad "undefined" string if found
        console.warn("App.tsx: Found 'undefined' string in localStorage key 'currentViewPubGPId'. Removing it.");
        localStorage.removeItem('currentViewPubGPId');
    }
  }, [pubToView]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Background>
        <Router>
          <Box 
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100%',
            }}
          >
            <Routes>
              <Route path="/" element={
                <Home 
                  setTitle={setTitle}
                  search={search}
                  setSearch={setSearch}
                  setLatitude={setLatitude}
                  setLongitude={setLongitude}
                  setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                  loading={loading}
                  setLoading={setLoading}
                />
              } />
              <Route path="/search-area" element={
                <SearchArea
                  setTitle={setTitle}
                  setLatitude={setLatitude}
                  setLongitude={setLongitude}
                  setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                  setLoading={setLoading}
                />
              } />
              <Route path="/submit" element={<SubmitGarden3D />} />
              <Route path="/view_pub" element={<ViewPubWrapper />} />
              <Route path="/pub/:googlePlaceId" element={<ViewPubWrapper />} />
              <Route path="/pubs" element={
                <FindPubs
                  search={search}
                  setSearch={setSearch}
                  latitude={latitude}
                  longitude={longitude}
                  setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                  loading={loading}
                  setLoading={setLoading}
                />
              } />
              <Route path="/find-pubs" element={
                <FindPubs
                  search={search}
                  setSearch={setSearch}
                  latitude={latitude}
                  longitude={longitude}
                  setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                  loading={loading}
                  setLoading={setLoading}
                />
              } />
              <Route path="/about" element={
                <About
                  setTitle={setTitle}
                  setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                />
              } />
              <Route path="/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ModerationAdmin />} />
              <Route path="/sunny-pubs-in/:encodedCity" element={<CityLandingPage />} />
              <Route path="/terms-of-use" element={
                <TermsOfUse
                  setTitle={setTitle}
                  setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                />
              } />
              <Route path="/privacy-policy" element={
                <PrivacyPolicy
                  setTitle={setTitle}
                  setShowBurgerMenuIcon={setShowBurgerMenuIcon}
                />
              } />

              {/* Protected Admin Routes */}
              <Route element={<AdminGuard />}>
                <Route path="/admin/moderation" element={<ModerationAdmin />} />
              </Route>
              
              <Route path="*" element={<NotFound />} />
            </Routes>
            <InstallPWA />
          </Box>
        </Router>
      </Background>
    </ThemeProvider>
  );
};

export default App;
